import React, { memo, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { areEqual } from 'Utils/equalityChecks';

import { Spinner } from 'Components/Spinner';
import { PurpleButton } from 'Components/Button/PurpleButton';
import { DeleteToast } from 'Components/People/PeopleTabs';
import { DeleteCardModal } from 'Components/People/PeopleTabs/PeopleList';
import { NonAppCrewModal } from 'Containers/People/PeopleTabs/NonAppCrewTab';

import { useUser } from 'Context/User';
import { UserModel } from 'Containers/User/Models/UserModel';
import { isCompanyAdmin } from 'Utils/roles';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import {
  crewInitialsSelector,
  totalCrewSelector,
  selectedCrewSelector,
  crewDeletedSelector,
  fetchingCrewSelector,
} from '../selectors';
import {
  listCrew,
  setSelectedCrew,
  deleteCrew,
  setCrewDeleted,
} from '../actions';

import classes from './nonAppCrewSection.module.css';

const NonAppCrewSectionContainer = () => {
  const dispatch = useDispatch();

  const user: UserModel = useUser();
  const [isAdmin] = useState(isCompanyAdmin(user.roles));

  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);
  const crewInitials = useSelector(crewInitialsSelector, areEqual);
  const totalCrew = useSelector(totalCrewSelector, areEqual);
  const selectedCrew = useSelector(selectedCrewSelector, areEqual);
  const crewDeleted = useSelector(crewDeletedSelector, areEqual);
  const fetching = useSelector(fetchingCrewSelector, areEqual);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showDeletedToast, setShowDeletedToast] = useState(false);

  const getCrew = useCallback(() => {
    if (firstCompanyId) {
      dispatch(listCrew(firstCompanyId));
    }
  }, [firstCompanyId, dispatch]);

  useEffect(() => {
    if (firstCompanyId) {
      dispatch(listCrew(firstCompanyId));
    }
  }, [firstCompanyId, dispatch]);

  useEffect(() => {
    if (crewDeleted) {
      dispatch(listCrew(firstCompanyId));
      setShowDeletedToast(true);
      setTimeout(() => setShowDeletedToast(false), 1500);
      dispatch(setCrewDeleted(''));
    }
  }, [crewDeleted, firstCompanyId, dispatch]);

  const openAddModal = useCallback(() => {
    dispatch(setSelectedCrew(null));
    setIsModalOpen(true);
  }, [dispatch]);

  const modalCloseClick = useCallback(
    (e?: any) => {
      if (e?.preventDefault) e.preventDefault();
      setIsModalOpen(false);
      setIsDeleteModalOpen(false);
      dispatch(setSelectedCrew(null));
    },
    [dispatch]
  );

  const selectCardClick = useCallback(
    (crew: any) => {
      dispatch(setSelectedCrew(crew));
      setIsModalOpen(true);
    },
    [dispatch]
  );

  const deleteCardClick = useCallback((e: any) => {
    e.preventDefault();
    setIsModalOpen(false);
    setIsDeleteModalOpen(true);
  }, []);

  const deleteCrewClick = useCallback(
    (e: any) => {
      e.preventDefault();
      if (selectedCrew?.id) {
        dispatch(deleteCrew(firstCompanyId, selectedCrew.id));
      }
      setIsDeleteModalOpen(false);
    },
    [firstCompanyId, selectedCrew, dispatch]
  );

  const closeToast = useCallback((e: any) => {
    if (e) e.preventDefault();
    setShowDeletedToast(false);
  }, []);

  return (
    <section className={classes.crewSection}>
      <div className={classes.contentHeader}>
        <h2>Non-app Crew</h2>
        {isAdmin && (
          <PurpleButton className={classes.addButton} onClick={openAddModal}>
            Add +
          </PurpleButton>
        )}
      </div>

      {fetching && <Spinner loading />}

      {!fetching && totalCrew === 0 && (
        <div className="text-muted">No non-app crew members yet. Click &quot;Add +&quot; to create one.</div>
      )}

      {!fetching && crewInitials.length > 0 && (
        <div className={classes.listContainer}>
          {crewInitials
            .flatMap(({ crew }: any) => crew)
            .map((member: any) => (
              <div
                key={member.id}
                className={classes.crewCard}
                onClick={() => selectCardClick(member)}
                role="button"
                tabIndex={0}
                onKeyUp={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') selectCardClick(member);
                }}
              >
                <div className={classes.cardName}>{member.fullName}</div>
                <div className={classes.cardDetails}>
                  {member.email && <span>{member.email}</span>}
                  {member.primaryPhone && <span>{member.primaryPhone}</span>}
                  {member.laborRate != null && (
                    <span className={classes.rate}>{`$${member.laborRate}/hr`}</span>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}

      <NonAppCrewModal
        isOpen={isModalOpen}
        onClose={modalCloseClick}
        onSaved={getCrew}
        onDeactivate={deleteCardClick}
      />

      <DeleteCardModal
        id={selectedCrew?.id?.toString()}
        title="Non-app Crew"
        name={selectedCrew?.fullName || ''}
        isOpen={isDeleteModalOpen}
        modalCloseClick={modalCloseClick}
        onDeleteButtonClick={deleteCrewClick}
      />

      {crewDeleted && (
        <DeleteToast
          isDisplayed={showDeletedToast}
          message={`${selectedCrew?.firstName || 'Crew member'} Deleted`}
          closeToast={closeToast}
        />
      )}
    </section>
  );
};

NonAppCrewSectionContainer.defaultProps = {};

const NonAppCrewSectionContainerMemo = memo(NonAppCrewSectionContainer, areEqual);

export { NonAppCrewSectionContainerMemo as NonAppCrewSection };
